/**
 * Messaging utilities — one implementation shared by every role.
 *
 * Previously each chat surface shipped its own copy of formatRelativeTime /
 * getInitials / date-grouping, and they disagreed (some crashed on Firestore
 * Timestamp values, some rendered "Invalid Date"). This module is the single
 * source of truth.
 */

import type {
  MessageAttachment,
  MessagingMessage,
  MessagingRole,
  ThreadEntry,
} from './types';

/* ─── Dates ───────────────────────────────────────────────────────────────── */

/**
 * Normalises every timestamp shape the backend can produce:
 * ISO string, epoch ms/seconds, Date, Firestore Timestamp ({seconds}/toDate()).
 * Returns null instead of an Invalid Date so callers can branch safely.
 */
export function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    // Heuristic: 10-digit values are seconds, 13-digit are milliseconds.
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'object') {
    const anyVal = value as Record<string, unknown>;
    if (typeof anyVal.toDate === 'function') {
      try {
        const d = (anyVal.toDate as () => Date)();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }
    if (typeof anyVal.seconds === 'number') {
      const d = new Date(anyVal.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof anyVal._seconds === 'number') {
      const d = new Date((anyVal._seconds as number) * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

export function toTime(value: unknown): number {
  const d = toDate(value);
  return d ? d.getTime() : 0;
}

/** "Just now", "4m", "3h", "2d", then a short date. Compact for list rows. */
export function formatRelativeTime(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 0) return 'Now';
  const secs = Math.floor(diff / 1000);
  if (secs < 45) return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Clock time inside a bubble. */
export function formatMessageTime(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Full timestamp for tooltips / screen readers. */
export function formatFullTimestamp(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "Today" / "Yesterday" / "Mon, Mar 4" for date separators. */
export function formatDateSeparator(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/* ─── People ──────────────────────────────────────────────────────────────── */

export function getInitials(name?: string | null, fallback = '?'): string {
  const clean = (name || '').trim();
  if (!clean) return fallback;
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ROLE_LABELS: Record<string, string> = {
  applicant: 'Applicant',
  employer: 'Employer',
  referrer: 'Referrer',
  agent: 'Agent',
  admin: 'Admin',
  support: 'Support',
  system: 'System',
};

export function roleLabel(role?: MessagingRole | string): string {
  if (!role) return '';
  return ROLE_LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1);
}

/** Normalises the many role spellings the backend uses. */
export function normaliseRole(role?: string | null): MessagingRole {
  const r = (role || '').toLowerCase();
  if (r === 'user' || r === 'student' || r === 'applicant') return 'applicant';
  if (r === 'employer' || r === 'company') return 'employer';
  if (r === 'referrer' || r === 'partner') return 'referrer';
  if (r === 'agent' || r === 'sub_agent') return 'agent';
  if (r === 'admin' || r === 'staff') return 'admin';
  if (r === 'support') return 'support';
  if (r === 'system' || r === 'audit') return 'system';
  return 'unknown';
}

/* ─── Files ───────────────────────────────────────────────────────────────── */

export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic',
];

export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

/**
 * Chat Phase 0 (F6 interim): 1:1 chat uploads go DIRECTLY to Storage from
 * the browser, and storage.rules only accepts images (jpeg/png/webp/gif)
 * and PDFs under chat_attachments/. Offering anything else (Office docs,
 * text, HEIC) produced sends that always failed at upload. Until
 * attachments are brokered through the server, the 1:1 composer offers
 * exactly what Storage accepts. Support chat keeps the full list above —
 * its uploads are brokered by the ticket pipeline server-side.
 */
export const DIRECT_MESSAGE_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
];

export const DIRECT_MESSAGE_ATTACHMENT_ACCEPT = DIRECT_MESSAGE_ATTACHMENT_EXTENSIONS.join(',');
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * The reaction set offered on ticket comments (Chat Phase 3). Mirrors the
 * ticket system's shared picker (TicketChatComponents REACTION_CHOICES) —
 * the server toggles whatever emoji string it is sent, so this list is a
 * product choice, kept identical across both renderers.
 */
export const MESSAGE_REACTION_CHOICES = ['👍', '❤️', '😊', '🎉', '😢', '🙏'];

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImageAttachment(att: MessageAttachment): boolean {
  if (att.mimeType?.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|avif|heic)(\?|$)/i.test(att.url || att.name || '');
}

export function fileExtensionLabel(att: MessageAttachment): string {
  const fromMime = att.mimeType?.split('/')[1];
  const fromName = (att.name || '').split('.').pop();
  const label = (fromName && fromName.length <= 5 ? fromName : fromMime) || 'file';
  return label.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 5) || 'FILE';
}

export function attachmentKey(att: MessageAttachment, index: number): string {
  return att.id || `${att.url || att.name}-${index}`;
}

/* ─── Bodies ─────────────────────────────────────────────────────────────── */

function bodyFromPart(part: unknown, depth = 0): string {
  if (depth > 8) return '';
  if (part == null) return '';
  if (typeof part === 'string') {
    const trimmed = part.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}') && trimmed.includes('"ciphertext"')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && ('ciphertext' in parsed || 'iv' in parsed)) {
          return '[Message content unavailable]';
        }
      } catch {
        /* not JSON */
      }
    }
    return part;
  }
  if (typeof part === 'number' || typeof part === 'boolean') return String(part);
  if (Array.isArray(part)) {
    return part
      .map((item) => bodyFromPart(item, depth + 1))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof part === 'object') {
    const obj = part as Record<string, unknown>;
    if ('iv' in obj && 'ciphertext' in obj && 'authTag' in obj) {
      return '[Message content unavailable]';
    }
    for (const key of ['text', 'value', 'body', 'content', 'html']) {
      if (obj[key] != null) return bodyFromPart(obj[key], depth + 1);
    }
    const children = obj.children ?? obj.blocks;
    if (children != null) return bodyFromPart(children, depth + 1);
    try {
      const serialized = JSON.stringify(obj);
      if (serialized && serialized !== '{}' && serialized !== '[]') {
        if (serialized.includes('"ciphertext"') && (serialized.includes('"iv"') || serialized.includes('"authTag"'))) {
          return '[Message content unavailable]';
        }
        return serialized;
      }
    } catch {
      /* circular reference — ignore */
    }
  }
  return '';
}

/**
 * Coerces every body shape the backend can produce (plain text, rich-content
 * object/array, number…) into a string so rendering, quoting and previews
 * never assume `typeof body === 'string'`.
 */
export function messageBodyText(value: unknown): string {
  return bodyFromPart(value).trim();
}

/** Human-readable preview for conversation rows. */
export function messagePreview(message?: MessagingMessage | null): string {
  if (!message) return '';
  const text = messageBodyText(message.body);
  if (text) return text;
  const count = message.attachments?.length || 0;
  if (count === 1) return `📎 ${message.attachments?.[0].name || 'Attachment'}`;
  if (count > 1) return `📎 ${count} attachments`;
  return '';
}

/* ─── Links ───────────────────────────────────────────────────────────────── */

const URL_RE = /(https?:\/\/[^\s<>"')]+)/gi;

/** Splits a body into plain-text and link segments for safe rendering. */
export function splitLinks(text: string): Array<{ text: string; href?: string }> {
  const str = typeof text === 'string' ? text : (text ? String(text) : '');
  if (!str) return [];
  const out: Array<{ text: string; href?: string }> = [];
  let lastIndex = 0;
  for (const match of str.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    if (start > lastIndex) out.push({ text: str.slice(lastIndex, start) });
    // Trailing punctuation should not be swallowed into the href.
    let url = match[0];
    const trailing = typeof url === 'string' ? url.match(/[.,;:!?)\]}]+$/) : null;
    let tail = '';
    if (trailing) {
      tail = trailing[0];
      url = url.slice(0, url.length - tail.length);
    }
    out.push({ text: url, href: url });
    if (tail) out.push({ text: tail });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < str.length) out.push({ text: str.slice(lastIndex) });
  return out;
}

export function firstLink(text: string): string | null {
  const str = typeof text === 'string' ? text : (text ? String(text) : '');
  if (!str) return null;
  const match = str.match(URL_RE);
  return match ? match[0] : null;
}

export function linkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/* ─── Thread grouping ─────────────────────────────────────────────────────── */

const GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface BuildThreadOptions {
  /** Ids/roles considered "me" — used to place bubbles right vs left. */
  isOwnMessage: (message: MessagingMessage) => boolean;
  /** Index of the first unread message; renders an unread divider. */
  firstUnreadIndex?: number | null;
  unreadCount?: number;
}

/**
 * Converts a flat, chronologically sorted message array into renderable
 * entries: date separators, an unread divider, and messages annotated with
 * grouping metadata. Pure + memo-friendly.
 */
export function buildThreadEntries(
  messages: MessagingMessage[],
  { isOwnMessage, firstUnreadIndex = null, unreadCount = 0 }: BuildThreadOptions
): ThreadEntry[] {
  const entries: ThreadEntry[] = [];
  let lastDateKey = '';

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    const date = toDate(message.createdAt);
    const dateKey = date ? date.toDateString() : '';

    if (dateKey && dateKey !== lastDateKey) {
      entries.push({ kind: 'date', key: `date-${dateKey}`, date: date as Date });
      lastDateKey = dateKey;
    }

    if (firstUnreadIndex != null && i === firstUnreadIndex && unreadCount > 0) {
      entries.push({ kind: 'unread', key: 'unread-divider', count: unreadCount });
    }

    const prev = messages[i - 1];
    const next = messages[i + 1];
    const sameAuthor = (a?: MessagingMessage, b?: MessagingMessage) =>
      !!a &&
      !!b &&
      !a.isSystem &&
      !b.isSystem &&
      (a.authorId || a.authorName) === (b.authorId || b.authorName) &&
      !!a.isInternal === !!b.isInternal;

    const withinWindow = (a?: MessagingMessage, b?: MessagingMessage) => {
      if (!a || !b) return false;
      const ta = toTime(a.createdAt);
      const tb = toTime(b.createdAt);
      if (!ta || !tb) return false;
      return Math.abs(tb - ta) < GROUP_WINDOW_MS;
    };

    const prevSameDay = prev ? toDate(prev.createdAt)?.toDateString() === dateKey : false;
    const nextSameDay = next ? toDate(next.createdAt)?.toDateString() === dateKey : false;

    const grouped = !!prev && prevSameDay && sameAuthor(prev, message) && withinWindow(prev, message);
    const lastOfGroup = !(next && nextSameDay && sameAuthor(message, next) && withinWindow(message, next));

    entries.push({
      kind: 'message',
      key: message.id || `msg-${i}`,
      message,
      isOwn: isOwnMessage(message),
      grouped,
      lastOfGroup,
    });
  }

  return entries;
}

/** Case-insensitive match across the searchable fields of a conversation row. */
export function matchesQuery(query: string, ...fields: Array<string | undefined | null>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((field) => (field || '').toLowerCase().includes(q));
}

/** Stable dedupe by id, keeping the last occurrence (server copy wins). */
/**
 * Conversation-list order (Chat Phase 4): a viewer's pinned
 * conversations sort before everything else; within each group the
 * newest activity wins. Surfaces that order server-side apply the same
 * rule when they re-sort client-side, so every inbox reads identically.
 */
export function compareConversationListOrder(
  a: { pinned?: boolean; timestamp?: unknown },
  b: { pinned?: boolean; timestamp?: unknown },
): number {
  const pinDelta = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
  if (pinDelta !== 0) return pinDelta;
  return toTime(b.timestamp) - toTime(a.timestamp);
}

export function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const map = new Map<string, T>();
  for (const item of items) map.set(item.id, item);
  return Array.from(map.values());
}

export function sortChronologically(messages: MessagingMessage[]): MessagingMessage[] {
  return [...messages].sort((a, b) => toTime(a.createdAt) - toTime(b.createdAt));
}
